import { BaseApi } from "@/api/index";
import { AxiosResponse } from "axios";

const baseUrl = process.env.REACT_APP_BACKEND_URL || "";

export interface AIEvents {
  title: string;
  description: string;
  date: string; // ex: "09/23/2024"
  author: string;
  starting_page: number | string | null;
  ending_page: number | string | null;
}

export interface AISummaryData {
  documentId: string;
  knowledgeBaseId: string;
  dataSourceId: string;
  cleaned_events: AIEvents[];
  result: AIEvents[];
  requestId: string;
  summary: {
    total_extracted: number;
    after_cleaning: number;
    final_unique: number;
    duplicates_removed: number;
  };
}

export interface Source {
  id: number;

  case: number;
  title: string;
  institution: string;
  description: string;
  type: string;
  start_date: string | null;
  end_date: string | null;
  total_pages: string | null;
  file: File | string | null;
  requires_text_extraction: boolean;
  extracted_text: string | null;
  ocr_task_status?: string | null;
  ocr_task_status_message?: string | null;
  ocr_task_id?: string | null;
  use_bates_numbers: boolean;
  source_starting_bates_number: string;
  corresponding_page_number: string | null;

  file_uploaded_date?: string | null;
  created_date: string;
  updated_date: string | null;
  deleted_at: string | null;
  created_by: number;
  updated_by: number | null;
  deleted_by: number | null;

  ai_summary_status?: string | null;
  ai_summary_data?: AISummaryData | string | null;
  ai_summary_error_message?: string | null;
  ai_summary_error_traceback?: string | null;

  version: number; // For optimistic locking
}

// AI processing request interface
export interface ProcessAiRequest {
  is_new_file?: boolean;
}

// AI processing response interface
export interface ProcessAiResponse {
  message: string;
  task_id: string;
  source_id: number;
  status: string;
}

// AI status response interface
export interface AiStatusResponse {
  source_id: number;
  ai_summary_status: string | null;
  ai_summary_data: AISummaryData | string | null;
  ai_summary_error_message: string | null;
  ai_summary_error_traceback: string | null;
}

// AI summary data save request interface
export interface SaveAiSummaryDataRequest {
  ai_summary_data: AISummaryData;
}

// AI summary data save response interface
export interface SaveAiSummaryDataResponse {
  message: string;
  source_id: number;
  ai_summary_data: AISummaryData | string | null;
}

class SourceApi<T = unknown> extends BaseApi<T> {
  constructor() {
    super({ apiBaseUrl: baseUrl, base: "api/sources" });
  }

  totalPages({
    token,
    params,
  }: {
    token?: string;
    params: Record<string, unknown>;
  }) {
    return this.request<unknown, AxiosResponse<{ total_pages__sum: number }>>({
      method: "GET",
      url: "/total_pages/",
      token,
      params: params,
    });
  }

  /**
   * Trigger AI processing for a source document
   * @param sourceId - The ID of the source document
   * @param token - Authentication token
   * @param data - Request data including is_new_file flag
   * @returns Promise with task information
   */
  processAi({
    sourceId,
    token,
    data,
  }: {
    sourceId: number;
    token?: string;
    data: ProcessAiRequest;
  }) {
    return this.create<ProcessAiRequest, AxiosResponse<ProcessAiResponse>>({
      url: `/${sourceId}/process_ai/`,
      token,
      data,
    });
  }

  /**
   * Get the current AI processing status for a source document
   * @param sourceId - The ID of the source document
   * @param token - Authentication token
   * @returns Promise with AI status information
   */
  getAiStatus({ sourceId, token }: { sourceId: number; token?: string }) {
    return this.request<unknown, AxiosResponse<AiStatusResponse>>({
      method: "GET",
      url: `/${sourceId}/ai_status/`,
      token,
    });
  }

  /**
   * Reset the AI processing status for a source document
   * @param sourceId - The ID of the source document
   * @param token - Authentication token
   * @returns Promise with reset confirmation
   */
  resetAiStatus({ sourceId, token }: { sourceId: number; token?: string }) {
    return this.request<
      unknown,
      AxiosResponse<{
        message: string;
        source_id: number;
        ai_summary_status: string;
      }>
    >({
      method: "POST",
      url: `/${sourceId}/reset_ai_status/`,
      token,
    });
  }

  /**
   * Save AI summary data for a source document
   * @param sourceId - The ID of the source document
   * @param token - Authentication token
   * @param data - Request data containing ai_summary_data
   * @returns Promise with save confirmation
   */
  saveAiSummaryData({
    sourceId,
    token,
    data,
  }: {
    sourceId: number;
    token?: string;
    data: SaveAiSummaryDataRequest;
  }) {
    return this.create<
      SaveAiSummaryDataRequest,
      AxiosResponse<SaveAiSummaryDataResponse>
    >({
      url: `/${sourceId}/save_ai_summary_data/`,
      token,
      data,
    });
  }
}

class LegacySourceApi<T = unknown> extends BaseApi<T> {
  constructor() {
    super({ apiBaseUrl: baseUrl, base: "sources" });
  }
  downloadExcelList({ token, caseId }: { token?: string; caseId: number }) {
    return this.request({
      method: "get",
      url: `/download_sources_excel/${caseId}/`,
      token,
      responseType: "arraybuffer",
    });
  }
  downloadAllSources({ token, caseId }: { token?: string; caseId: number }) {
    return this.request({
      method: "get",
      url: `/download_sources/${caseId}/`,
      token,
      responseType: "arraybuffer",
    });
  }
}

export const sourcesApi = new SourceApi<Source>();
export const legacySourcesApi = new LegacySourceApi();
